/**
 * <story-slides> — one picture at a time, chosen from the strip of tabs along the
 * bottom.
 *
 * The strip is built here rather than in Liquid, from the label each slide carries in
 * a data attribute: a section cannot read a theme block's settings, so the wording is
 * only reliably in hand inside the block itself. The rest is connecting it — setting
 * which slide is shown, moving focus the way a tab list is expected to, and, when the
 * section is set to play itself, advancing it.
 *
 * Playing itself is the part worth being careful about. A slideshow that keeps
 * advancing while it is off screen wastes work and, worse, has moved on by the time
 * the reader arrives; one that keeps advancing under the pointer takes the slide away
 * mid-sentence. So it runs only while on screen and unattended, and one click stops
 * it for good — after someone has chosen a slide, moving off it is no longer helpful.
 */
(() => {
  class StorySlides extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.slides.length) return;

      this.index = 0;
      this.#buildTabs();
      this.classList.add('is-ready');
      this.#show(0);

      // The copy's entrance waits for the section to be looked at. The first slide is
      // shown the moment the script runs, so on a section below the fold the animation
      // would otherwise play to an empty room and be over before anyone scrolled to
      // it — which is exactly what it looked like: visible on a reload at the top,
      // never on the way down. Once only; replaying it on every pass would be a
      // section that fidgets.
      this.seenObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          this.seenObserver.disconnect();
          this.classList.add('is-seen');
        },
        { threshold: 0.25 }
      );
      this.seenObserver.observe(this);

      // A strip of one tab is a control that cannot do anything, and the room held
      // clear for it at the foot of the slide is given back.
      if (this.slides.length < 2) {
        this.classList.add('slides--single');
        return;
      }

      this.onClick = (event) => {
        const arrow = event.target.closest('[data-slides-prev], [data-slides-next]');
        if (arrow) {
          this.#stopPlaying();
          const direction = arrow.hasAttribute('data-slides-next') ? 1 : -1;
          this.#show((this.index + direction + this.slides.length) % this.slides.length);
          return;
        }
        const tab = event.target.closest('[data-slide-target]');
        if (!tab || !this.tabs.length) return;
        this.#stopPlaying();
        this.#show(this.tabs.indexOf(tab));
      };

      // The roving-tabindex pattern a tab list is expected to follow: one stop in the
      // tab order, arrows between the tabs inside it.
      this.onKey = (event) => {
        if (!event.target.closest('[data-slide-target]')) return;
        const keys = {
          ArrowRight: this.index + 1,
          ArrowDown: this.index + 1,
          ArrowLeft: this.index - 1,
          ArrowUp: this.index - 1,
          Home: 0,
          End: this.tabs.length - 1,
        };
        if (!(event.key in keys) || !this.tabs.length) return;
        event.preventDefault();
        this.#stopPlaying();
        const next = (keys[event.key] + this.tabs.length) % this.tabs.length;
        this.#show(next);
        this.tabs[next].focus();
      };

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);

      this.#setupPlaying();
    }

    disconnectedCallback() {
      clearTimeout(this.leaveTimer);
      this.#stopPlaying();
      this.observer?.disconnect();
      this.seenObserver?.disconnect();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* One tab per slide, labelled by the slide. A slide with nothing in its label
       field falls back to its number rather than to an unlabelled rule nobody can
       aim at. */
    #buildTabs() {
      const list = this.querySelector('[data-slides-tabs]');
      this.tabs = [];
      if (!list) return;

      list.replaceChildren();
      this.slides.forEach((slide, i) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'slides__tab';
        tab.setAttribute('role', 'tab');
        tab.dataset.slideTarget = slide.dataset.slide || String(i);
        tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        tab.tabIndex = i === 0 ? 0 : -1;

        const rule = document.createElement('span');
        rule.className = 'slides__rule';
        rule.setAttribute('aria-hidden', 'true');

        const label = document.createElement('span');
        label.className = 'slides__label';
        label.textContent = (slide.dataset.slideLabel || '').trim() || String(i + 1);

        tab.append(rule, label);
        list.append(tab);
        this.tabs.push(tab);
      });
    }

    #show(index) {
      const leaving = this.slides[this.index];
      this.index = index;

      // The slide on its way out needs a state of its own. Simply dropping
      // `.is-active` would return it to where slides wait to come in, so with the
      // sliding effect it would reverse back out the side the new one arrives from.
      this.slides.forEach((slide) => slide.classList.remove('is-leaving'));
      if (leaving && leaving !== this.slides[index]) {
        leaving.classList.add('is-leaving');
        clearTimeout(this.leaveTimer);
        // Cleared once it is off screen and hidden, so it is back in the waiting
        // position before it is ever asked to come in again. Comfortably longer than
        // the transition; by then nothing is visible to jump.
        this.leaveTimer = setTimeout(() => leaving.classList.remove('is-leaving'), 700);
      }

      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.tabs?.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.#restartFill();
      this.#restartTimer();
    }

    /* The fill on the active rule is a CSS animation, and CSS will not replay one
       that is already running on an element it considers unchanged. Taking the class
       off, forcing a reflow and putting it back is what gives each slide its own run
       of the timer. */
    #restartFill() {
      if (!this.playing) return;
      this.classList.remove('slides--playing');
      void this.offsetWidth;
      this.classList.add('slides--playing');
    }

    #setupPlaying() {
      if (this.dataset.autoplay !== 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.interval = Number(this.dataset.interval) || 6000;
      this.playing = true;
      this.classList.add('slides--playing');

      // Only while on screen: a slideshow that runs in an unseen part of the page has
      // already moved on by the time it is read.
      this.observer = new IntersectionObserver(
        ([entry]) => this.#setPaused(!entry.isIntersecting),
        { threshold: 0.35 }
      );
      this.observer.observe(this);

      // A background tab throttles timers unevenly; stopping is honest about it.
      this.onVisibility = () => this.#setPaused(document.hidden);
      document.addEventListener('visibilitychange', this.onVisibility);

      // Under the pointer or holding focus, the reader is on this slide.
      this.addEventListener('pointerenter', () => this.#setPaused(true));
      this.addEventListener('pointerleave', () => this.#setPaused(false));
      this.addEventListener('focusin', () => this.#setPaused(true));
      this.addEventListener('focusout', () => this.#setPaused(false));

      this.#restartTimer();
    }

    #setPaused(paused) {
      if (!this.playing) return;
      this.classList.toggle('is-paused', paused);
      if (paused) clearTimeout(this.timer);
      else this.#restartTimer();
    }

    #restartTimer() {
      if (!this.playing) return;
      clearTimeout(this.timer);
      if (this.classList.contains('is-paused')) return;
      this.timer = setTimeout(() => this.#show((this.index + 1) % this.slides.length), this.interval);
    }

    #stopPlaying() {
      if (!this.playing) return;
      this.playing = false;
      clearTimeout(this.timer);
      this.classList.remove('slides--playing', 'is-paused');
      this.observer?.disconnect();
    }
  }

  if (!customElements.get('story-slides')) {
    customElements.define('story-slides', StorySlides);
  }
})();
